import { Component, Inject, Input, OnChanges, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CourseCatalogLandingPageOverview } from 'app/library/course-catalog-landing-page/models/course-catalog-landing-page-overview.model';
import { CourseCatalogPostingStream } from 'app/library/course-catalog-landing-page/models/course-catalog-posting-stream.model';
import { CourseCatalogLandingPageApiService } from 'app/library/course-catalog-landing-page/services/course-catalog-landing-page.api-service';
import { ExtendedAttributeFilter } from 'app/library/extended-attributes/models/extended-attribute-filter.model';
import { ExtendedAttributeApiService } from 'app/library/extended-attributes/services/extended-attribute.api-service';
import { PostingTypeEnum } from 'app/library/posting/models/posting-type.enum';
import { PostingType } from 'app/library/posting/models/posting-type.model';
import { PostingTypeApiService } from 'app/library/posting/services/posting-type.api-service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { combineLatest, firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { CourseCatalogCategoryType } from '../../enums/course-catalog-category-type.enum';
import { CourseCatalogService } from '../../services/course-catalog.service';

@Component({
    selector: 'app-course-catalog',
    templateUrl: './course-catalog.component.html',
    styleUrls: ['./course-catalog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CourseCatalogComponent implements OnChanges {
    @Input()
    courseStructureCategoryId!: string;

    @Input()
    isInsideDialog: boolean = false;

    isLoading = true;
    courseCatalogLandingPageOverview: CourseCatalogLandingPageOverview;
    postings: CourseCatalogPostingStream[] = [];
    postingTypes: PostingType[] = [];
    filterForm: FormGroup;
    filterValues: ExtendedAttributeFilter[] = [];
    dateRangeLimits: Record<string, { min: Date | null; max: Date | null }> = {};
    hasUnfilteredPostings = false;
    isExtendedAttributesFeatureEnabled = false;

    constructor(
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        private _postingTypeApiService: PostingTypeApiService,
        private _courseCatalogApiService: CourseCatalogLandingPageApiService,
        private _catalogService: CourseCatalogService,
        private _extendedAttributeApiService: ExtendedAttributeApiService,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _router: Router,
        private fb: FormBuilder
    ) {
        this.isExtendedAttributesFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.ExtendedAttributes);

        this.filterForm = this.fb.group({
            language: [null],
            postingType: [null],
        });

        combineLatest([
            this.filterForm.get('language')!.valueChanges.pipe(startWith(null)),
            this.filterForm.get('postingType')!.valueChanges.pipe(startWith(null)),
            this.filterForm.valueChanges.pipe(startWith(this.filterForm.value)),
        ])
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(([selectedLanguage, selectedPostingType, allFilters]) => {
                if (this.isLoading) return;

                // /categories/{id}/postings is only valid for offerings categories
                if (this.courseCatalogLandingPageOverview?.landingPage.category.type !== CourseCatalogCategoryType.OFFERINGS) {
                    return;
                }

                const extendedFilters: Record<string, any> = {};

                this.filterValues.forEach((filter) => {
                    const name = filter.name;

                    if (filter.type === 'value_list' && allFilters[name]) {
                        extendedFilters[name] = [allFilters[name]];
                    }

                    if (filter.type === 'number_range') {
                        if (allFilters[name + '_min'] !== null) extendedFilters[name + '_min'] = allFilters[name + '_min'];
                        if (allFilters[name + '_max'] !== null) extendedFilters[name + '_max'] = allFilters[name + '_max'];
                    }

                    if (filter.type === 'date_range') {
                        const minDate = this.formatDateFilterValue(allFilters[name + '_min']);
                        const maxDate = this.formatDateFilterValue(allFilters[name + '_max']);

                        if (minDate) extendedFilters[name + '_min'] = minDate;
                        if (maxDate) extendedFilters[name + '_max'] = maxDate;
                    }
                });

                this.loadPostings(selectedLanguage, selectedPostingType, extendedFilters);
            });
    }

    async ngOnChanges(): Promise<void> {
        if (!this.courseStructureCategoryId) return;

        this.isLoading = true;
        this.hasUnfilteredPostings = false;
        this.postings = [];

        try {
            this.courseCatalogLandingPageOverview = await this._courseCatalogApiService.getCourseCatalogLandingPageOverview(this.courseStructureCategoryId).toPromise();

            const categoryType = this.courseCatalogLandingPageOverview.landingPage.category.type;

            if (categoryType === CourseCatalogCategoryType.SUB_CATEGORIES) {
                const categorySections = this.courseCatalogLandingPageOverview.categorySections;

                // Avoid firstValueFrom(forkJoin([])) which throws EmptyError and leaves the shimmer stuck
                if (categorySections.length > 0) {
                    const postingStreams = await firstValueFrom(
                        forkJoin(
                            categorySections.map((section) => {
                                if (section.type === CourseCatalogCategoryType.UNINITIALIZED) {
                                    return of([]);
                                }

                                return this._courseCatalogApiService.getCategoryPostingStream(section.id).pipe(catchError(() => of([])));
                            })
                        )
                    );

                    categorySections.forEach((section, index) => {
                        section.postingStream = postingStreams[index];
                    });
                }
            } else if (categoryType === CourseCatalogCategoryType.OFFERINGS) {
                const allowedPostingTypes = new Set<string>([PostingTypeEnum.Course, PostingTypeEnum.Program, PostingTypeEnum.E_learning]);
                this.postingTypes = (await this._postingTypeApiService.getPostingTypes('course_offering')).filter((type) => allowedPostingTypes.has(type.name));
                if (this.isExtendedAttributesFeatureEnabled) {
                    this.filterValues = await this._extendedAttributeApiService.fetchCourseOfferingFilterValues(true);
                } else {
                    this.filterValues = [];
                }
                this.dateRangeLimits = {};

                this.filterValues.forEach((filter) => {
                    if (filter.type === 'number_range' || filter.type === 'date_range') {
                        if (!this.filterForm.contains(`${filter.name}_min`)) this.filterForm.addControl(`${filter.name}_min`, new FormControl(null));
                        if (!this.filterForm.contains(`${filter.name}_max`)) this.filterForm.addControl(`${filter.name}_max`, new FormControl(null));

                        if (filter.type === 'date_range') {
                            this.dateRangeLimits[filter.name] = {
                                min: this.parseDateBoundary(filter.min),
                                max: this.parseDateBoundary(filter.max),
                            };
                        }
                    } else {
                        if (!this.filterForm.contains(filter.name)) this.filterForm.addControl(filter.name, new FormControl(null));
                    }
                });

                await this.loadPostings();
                this.hasUnfilteredPostings = this.postings.length > 0;
            }
        } finally {
            this.isLoading = false;
        }
    }

    navigateToCategory(categoryId: string): void {
        this._catalogService.setSelectedCategoryId(categoryId);
        this._router.navigate([`catalog/category/${categoryId}`]);
    }

    private async loadPostings(language?: string, postingType?: string, extendedFilters?: Record<string, any>): Promise<void> {
        if (!this.courseStructureCategoryId) return;

        if (this.courseCatalogLandingPageOverview?.landingPage.category.type !== CourseCatalogCategoryType.OFFERINGS) {
            return;
        }

        this.postings = await this._courseCatalogApiService
            .getCategoryWithOfferingsPostings(this.courseStructureCategoryId, '', 100, 1, language, postingType, extendedFilters)
            .toPromise();
    }

    hasSelection(controlName: string): boolean {
        const value = this.filterForm?.get(controlName)?.value;

        if (Array.isArray(value)) return value.length > 0;

        return value !== null && value !== undefined && value !== '';
    }

    get hasActiveFilters(): boolean {
        if (!this.filterForm) return false;

        return Object.values(this.filterForm.value).some((value) => {
            if (Array.isArray(value)) return value.length > 0;

            return value !== null && value !== undefined && value !== '';
        });
    }

    get showNoResultsFoundState(): boolean {
        return this.hasUnfilteredPostings && this.hasActiveFilters;
    }

    get showCategoryLevelEmptyState(): boolean {
        if (!this.courseCatalogLandingPageOverview) {
            return false;
        }

        const categoryType = this.courseCatalogLandingPageOverview.landingPage.category.type;

        if (categoryType === CourseCatalogCategoryType.UNINITIALIZED) {
            return true;
        }

        return categoryType === CourseCatalogCategoryType.SUB_CATEGORIES && this.courseCatalogLandingPageOverview.categorySections.length === 0;
    }

    getPostingHoverPosition(index: number): 'left' | 'center' | 'right' {
        const columnsPerRow = this.getColumnsPerRow();
        const totalPostings = this.postings.length;
        const rowStartIndex = Math.floor(index / columnsPerRow) * columnsPerRow;
        const itemsInRow = Math.min(columnsPerRow, totalPostings - rowStartIndex);
        const positionInRow = index - rowStartIndex;

        if (itemsInRow === 1) {
            return 'right';
        }

        if (positionInRow === 0) {
            return 'right';
        }

        if (positionInRow === itemsInRow - 1) {
            return 'left';
        }

        return 'center';
    }

    private getColumnsPerRow(): number {
        const viewportWidth = window.innerWidth;

        if (viewportWidth >= 960) {
            return 3;
        }

        if (viewportWidth >= 600) {
            return 2;
        }

        return 1;
    }

    private formatDateFilterValue(value: any): string | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return value.toISOString().split('T')[0];
        }

        if (typeof value === 'string') {
            return value;
        }

        if (value?.format) {
            return value.format('YYYY-MM-DD');
        }

        return null;
    }

    private parseDateBoundary(value: string | null | undefined): Date | null {
        if (!value) {
            return null;
        }

        const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (dateOnlyMatch) {
            const year = Number(dateOnlyMatch[1]);
            const monthIndex = Number(dateOnlyMatch[2]) - 1;
            const day = Number(dateOnlyMatch[3]);

            return new Date(year, monthIndex, day);
        }

        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
}
